import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { updatePageState } from "../store";

export type RemoteFailurePhase = "manifest" | "module" | "render";

interface RemoteFailurePanelProps {
  remote: string;
  exposedModule: string;
  phase: RemoteFailurePhase;
  error: unknown;
  onRetry?: () => void;
}

const REMOTE_LABELS: Record<string, string> = {
  art_liveconnect: "Live Connect",
  art_agentlab: "Agent Lab",
  art_appstudio: "App Studio",
  art_superadmin: "Super Admin",
  art_orchestrator: "Orchestrator",
};

const remoteLabel = (remote: string) =>
  REMOTE_LABELS[remote] || remote || "This application";

const errorDetails = (error: unknown) => {
  if (error instanceof Error) {
    const cause =
      "cause" in error && error.cause
        ? `\nCause: ${String(error.cause)}`
        : "";
    return `${error.name}: ${error.message}${cause}${error.stack ? `\n\n${error.stack}` : ""}`;
  }
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
};

export default function RemoteFailurePanel({
  remote,
  exposedModule,
  phase,
  error,
  onRetry,
}: RemoteFailurePanelProps) {
  const details = errorDetails(error);
  const label = remoteLabel(remote);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(updatePageState({ page_title: `${label} unavailable`, force_fullscreen: false }));
  }, [dispatch, label]);
  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 920, mx: "auto" }}>
      <Alert severity="error" sx={{ alignItems: "flex-start" }}>
        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {label} could not be loaded
            </Typography>
            <Typography variant="body2">
              The host is running, but the federated {label} application failed
              during its {phase} phase.
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            {onRetry && (
              <Button variant="contained" onClick={onRetry}>
                Retry loading
              </Button>
            )}
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Reload application
            </Button>
          </Stack>

          <details>
            <summary style={{ cursor: "pointer", fontWeight: 650 }}>
              Technical details
            </summary>
            <Box
              component="pre"
              sx={{
                mt: 1,
                mb: 0,
                p: 1.5,
                maxHeight: 280,
                overflow: "auto",
                borderRadius: 1,
                bgcolor: "rgba(0,0,0,.06)",
                fontSize: 12,
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
              }}
            >
              {`Remote: ${remote}\nModule: ${exposedModule}\nPhase: ${phase}\n\n${details}`}
            </Box>
          </details>
        </Stack>
      </Alert>
    </Box>
  );
}
